/**
 * Generic layout component to display setting information in a card
 */

import React from 'react'
import { Box, Grid, GridSize, Stack, styled, SxProps, useMediaQuery, useTheme } from '@mui/material'
import { Typography } from '@e3dc-react/shell/Elements/Typography'

interface Props {
  disabled?: boolean
}

const MainContainer = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: theme.palette.grey.grey06, // TODO: move to theme definition
  borderRadius: theme.shape.borderRadius,
  marginBottom: 12,
}))

const Description = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'flex-start',
}))

interface GridSizes {
  mdLeft?: GridSize
  mdRight?: GridSize
  smLeft?: GridSize
  smRight?: GridSize
  xsLeft?: GridSize
  xsRight?: GridSize
}

interface Props {
  title: string
  titleIcon?: React.ReactNode
  titleSx?: SxProps
  description?: string | React.ReactElement
  suffix?: React.ReactNode
  variant?: 'auto' | 'fullWidth'
  disabled?: boolean
  depth?: number
  className?: string
  isSingleLine?: boolean
  suffixSx?: SxProps
  gridSizes?: GridSizes
  children?: React.ReactNode
}

const SettingsBox: React.FC<Props> = ({
  title,
  titleIcon,
  titleSx,
  description,
  suffix,
  suffixSx,
  variant = 'auto',
  disabled = false,
  depth = 0,
  isSingleLine = false,
  gridSizes,
  children,
  ...rest
}) => {
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const backgroundColorEnabled = depth % 2 === 0 ? theme.palette.grey.grey07 : theme.palette.background.paper

  return (
    <MainContainer {...rest} sx={{ backgroundColor: backgroundColorEnabled, flexDirection: 'column' }}>
      <Grid container alignItems={'center'}>
        <Grid item xs={gridSizes?.xsLeft ?? (isSingleLine ? 8 : 12)} sm={gridSizes?.smLeft ?? 8} md={gridSizes?.mdLeft ?? 6}>
          <Description>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" display="flex" sx={titleSx}>
                {title}
              </Typography>
              {titleIcon}
            </Box>
            {description && typeof description === 'string' ? (
              <Typography variant="body2" mt={2}>
                {description}
              </Typography>
            ) : (
              description
            )}
          </Description>
        </Grid>
        <Grid item xs={gridSizes?.xsRight ?? (isSingleLine ? 4 : 12)} sm={gridSizes?.smRight ?? 4} md={gridSizes?.mdRight ?? 6}>
          <Stack sx={suffixSx ?? { flexDirection: 'row', justifyContent: downSm ? 'center' : 'end', alignItems: 'center' }}>{suffix}</Stack>
        </Grid>
      </Grid>
      {children && (
        <Box width="100%" mt={1}>
          {children}
        </Box>
      )}
    </MainContainer>
  )
}

export default SettingsBox
